import * as R from 'ramda';
import { useEffect, useState } from 'react';
import LangEnum from '../../lang/LangEnum';
import { handlerApiError } from '../../lib/basicRepository';
import { useTranslation } from '../../modules/translation/TranslationContext';
import { restApi } from '../../services/api';
import snackbarStore from '../../stores/Snackbar.store';
import { WithId } from '../../types/Base.types';
import { buildAutocompleteFilterUrl } from '../FormInputs/autocompletes/ListPickAutocomplete/ListPickAutocomplete.utils';
import { useListPick } from './useListPick';
import { SelectedData, UseOptimisticListPickProps } from './useOptimisticListPick.types';

/**
 * @description search selected items and set them in the first positions in the array.
 */
const useOptimisticListPick = <T extends WithId>({
  defaultValue,
  listPickProps,
  getInitialValuesQuery,
  customFetcherToInitialValues,
}: UseOptimisticListPickProps<T>) => {
  const { translate } = useTranslation();

  const [selectedData, setSelectedData] = useState<SelectedData<T>>({
    currentItems: [],
    newItems: [],
  });
  const [isLoadingInitialValues, setIsLoadingInitialValues] = useState(true);

  const listPickActions = useListPick<T>(
    customFetcherToInitialValues
      ? {
          queryField: listPickProps.queryField,
          customFetcher: listPickProps.customFetcher,
        }
      : {
          limit: 20,
          ...listPickProps,
        },
  );

  const getInitialValues = async () => {
    if (!defaultValue?.length) {
      setIsLoadingInitialValues(false);
      return;
    }

    try {
      const response = handlerApiError<{ data: T[] }>(
        customFetcherToInitialValues
          ? await customFetcherToInitialValues(defaultValue)
          : await restApi.get(
              buildAutocompleteFilterUrl({
                ids: defaultValue,
                url: listPickProps.url,
                idsAlias: listPickProps.idsAlias,
              }),
              getInitialValuesQuery,
            ),
      );

      if (response.ok && response.data?.data) {
        setSelectedData({
          currentItems: response.data.data,
          newItems: [],
        });
        return;
      }
      throw new Error();
    } catch (_err) {
      snackbarStore.handleMessage({
        message: translate(LangEnum.SNACKBAR_REFRESH_PAGE),
        type: 'error',
      });
    } finally {
      setIsLoadingInitialValues(false);
    }
  };

  const onChangeSelectedData = (item: T) => {
    const currentItemsFiltered = selectedData.currentItems.filter(({ id }) => id !== item.id);
    if (currentItemsFiltered.length !== selectedData.currentItems.length) {
      setSelectedData((value) => ({ ...value, currentItems: currentItemsFiltered }));
      return;
    }

    const newItemsFiltered = selectedData.newItems.filter(({ id }) => id !== item.id);
    if (newItemsFiltered.length !== selectedData.newItems.length) {
      setSelectedData((value) => ({ ...value, newItems: newItemsFiltered }));
      return;
    }

    setSelectedData((value) => ({ ...value, newItems: [...selectedData.newItems, item] }));
    return;
  };

  const addSelectedItems = (items: T[]) => {
    const newItems = R.uniqBy(({ id }) => id, [...selectedData.newItems, ...items]);
    setSelectedData((value) => ({ ...value, newItems }));
  };

  const onUpdateSelectedItem = (item: T) => {
    const updatedCurrentItems = selectedData.currentItems.map((currentItem) =>
      currentItem.id === item.id ? item : currentItem,
    );
    const updatedNewItems = selectedData.newItems.map((newItem) =>
      newItem.id === item.id ? item : newItem,
    );
    const newSelectedData = {
      currentItems: updatedCurrentItems,
      newItems: updatedNewItems,
    };
    setSelectedData(newSelectedData);
  };

  const loadMore = () => {
    if (listPickActions.hasNextPage && !listPickActions.loading) {
      listPickActions.next();
    }
  };

  const handleSearch = (query: string) => {
    listPickActions.clear();
    listPickActions.onSearch(query);
  };

  useEffect(() => {
    getInitialValues();
  }, []);

  const allOptions = [
    ...selectedData.currentItems,
    ...listPickActions.leftItems,
    ...selectedData.newItems,
  ];
  const filteredOptions = R.uniqBy(({ id }) => id, allOptions);

  return {
    addSelectedItems,
    isLoading: listPickActions.loading,
    isLoadingInitialValues,
    loadMore,
    onChangeSelectedData,
    onSearch: handleSearch,
    onUpdateSelectedItem,
    options: filteredOptions,
    reset: listPickActions.clear,
    selectedData,
  };
};

export default useOptimisticListPick;
